/**
 * Consortium Groups Resource
 * Lista grupos de consórcio disponíveis para venda (dados do Servopa)
 */

import { List } from '@/components/admin/list';
import { formatBrDateTime } from '@/lib/format';
import { useListContext, useStore } from 'ra-core';
import { PageSpinner } from '@/components/ui/page-spinner';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useState, useEffect, useMemo } from 'react';
import { GroupDetailModal } from './GroupDetailModal';
import { ChevronRight } from 'lucide-react';
import { CabecalhoPagina } from '../bento/ui';
import { MIN_ASSEMBLEIAS, useLanceResumo, type LanceResumo } from './InsightLance';
import { gruposQueContemplamRapido } from './contemplaRapido';
import {
  ORDENACAO_PADRAO,
  normalizarOrdenacao,
  type CampoDeOrdenacao,
} from './ordenacaoGrupos';
import {
  EMBUTIDO_PADRAO_PCT,
  FILTROS_AVANCADOS_VAZIOS,
  ROTULO_MODO_LANCE,
  creditoAlvoDoGrupo,
  grupoPassaNosFiltrosAvancados,
  modoUsaCreditoLiquido,
  type ContextoDoGrupo,
  type FiltrosAvancados,
  type ModoLance,
} from './filtrosAvancados';
import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { todasAsLinhas } from '@/components/atomic-crm/providers/supabase/todasAsLinhas';
import { MoedaTextoInput } from "@/components/ui/moeda-input";

type ConsortiumGroup = {
  id: number;
  group_number: string;
  segment: 'M' | 'I'; // M = Móveis/Automóveis, I = Imóveis
  months: number;
  assembly: number;
  bid: number;
  awarded: number;
  vacancies: string;
  flex: boolean;
  credit_min: number | null;
  credit_max: number | null;
  last_sync_at: string;
  created_at: string;
  updated_at: string;
  // Vêm de servopa_group_details via servopa_groups_enriched (parseado do raw_rules).
  // Cobertura: 100% dos grupos. .bid/.awarded acima estão NULL/0 — não usar.
  contemplados_sorteio: number | null;
  contemplados_lance: number | null;
};

const segmentoLabels: Record<string, string> = {
  M: 'Automóveis',
  I: 'Imóveis',
};

const parseVacancies = (value: unknown): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0;
  }
  if (typeof value !== 'string') return 0;

  const token = value.match(/\d[\d.,]*/)?.[0] || '';
  const digits = token.replace(/\D/g, '');
  if (!digits) return 0;

  const parsed = Number.parseInt(digits, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatarBRL = (valor: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  }).format(valor);

export const ConsortiumGroupList = () => (
  <List
    resource="servopa_groups_enriched"
    sort={{ field: 'group_number', order: 'ASC' }}
    perPage={1000}
    filters={[]}
    actions={false}
    disableSyncWithLocation
    pagination={false}
  >
    <GroupsListContent />
  </List>
);

const GroupsListContent = () => {
  const { data: groups, isPending, error, setFilters } = useListContext<ConsortiumGroup>();
  const [searchTerm, setSearchTerm] = useState('');
  const busca = searchTerm;
  const [segmentoFilter, setSegmentoFilter] = useState<string>('all');
  const [flexOnlyFilter, setFlexOnlyFilter] = useState<'all' | 'sim' | 'nao'>('all');
  const [selectedGroupNumber, setSelectedGroupNumber] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  /**
   * A ordenação fica salva no store do ra-core (localStorage, prefixo "CRM"
   * em CRM.tsx): o vendedor volta à aba e encontra a lista do mesmo jeito. O que vem salvo passa pela
   * normalização — pode ser de uma versão antiga da tela.
   */
  const [ordenacaoSalva, salvarOrdenacao] = useStore<unknown>('grupos.lista.ordenacao', ORDENACAO_PADRAO);
  const ordenacao = useMemo(() => normalizarOrdenacao(ordenacaoSalva), [ordenacaoSalva]);
  const { campo: sortBy, ordem: sortOrder } = ordenacao;
  const [prazoExato, setPrazoExato] = useState<string>(''); // Filtro dropdown de prazo exato
  /**
   * Filtros avançados num objeto só, porque a regra pura (filtrosAvancados.ts)
   * os lê em bloco. Um estado por campo obrigava a repetir a mesma lista de
   * dependências em três `useMemo` — esquecer um campo lá deixa a lista
   * parada, e o erro só aparece quando alguém usa aquele filtro específico.
   */
  const [filtros, setFiltros] = useState<FiltrosAvancados>(FILTROS_AVANCADOS_VAZIOS);
  const definirFiltro = <C extends keyof FiltrosAvancados>(campo: C, valor: FiltrosAvancados[C]) =>
    setFiltros((atual) => ({ ...atual, [campo]: valor }));
  const [currentPage, setCurrentPage] = useState(1);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [groupPrazos, setGroupPrazos] = useState<Map<number, number[]>>(new Map());
  const [groupParcelas, setGroupParcelas] = useState<Map<number, { min: number; max: number }>>(new Map());
  // % de lance embutido publicado por grupo (servopa_group_details). Só ele
  // permite converter o crédito líquido pedido no bruto que precisa ser
  // contratado — sem a linha do grupo, a conta cai nos 30% padrão.
  const [embutidoPorGrupo, setEmbutidoPorGrupo] = useState<Map<number, number>>(new Map());
  // Histórico de lance por grupo (migration 99). Duas leituras que decidem
  // estratégia e antes só existiam dentro da IA: quanto custa quando contempla,
  // e com que frequência contempla. A leitura é compartilhada com o insight do
  // topo — duas réguas de amostra diferentes na mesma tela seria incoerência
  // visível: o insight recomendaria um grupo que a lista trata como desconhecido.
  const lancePorGrupo = useLanceResumo();
  // Selo "Contempla rápido": 20% de maior chance por cota por mês em cada
  // segmento, entre os grupos com vaga (contemplaRapido.ts). Destaca, não
  // esconde (reunião de 14/10).
  const contemplaRapido = useMemo(() => {
    const comVaga = new Set(
      (groups ?? [])
        .filter((g) => parseVacancies(g.vacancies) > 0)
        .map((g) => parseInt(g.group_number)),
    );
    return gruposQueContemplamRapido(lancePorGrupo.values(), comVaga);
  }, [lancePorGrupo, groups]);
  /**
   * O histórico de lance do grupo, ou null quando a amostra não sustenta.
   *
   * Um único ponto de leitura pra ordenação e pros filtros — se cada um
   * aplicasse a própria régua de amostra, o filtro poderia deixar passar um
   * grupo que a ordenação considera desconhecido.
   */
  const historicoDoGrupo = useMemo(
    () => (numeroDoGrupo: string | number): LanceResumo | null => {
      const n = typeof numeroDoGrupo === 'string' ? parseInt(numeroDoGrupo) : numeroDoGrupo;
      const l = lancePorGrupo.get(n);
      if (!l || (l.assembleias_observadas ?? 0) < MIN_ASSEMBLEIAS) return null;
      return l;
    },
    [lancePorGrupo],
  );

  /** O grupo como a regra pura o enxerga: faixa de crédito + embutido dele. */
  const grupoParaFiltro = useMemo(
    () => (group: ConsortiumGroup) => ({
      credit_min: group.credit_min,
      credit_max: group.credit_max,
      lance_embutido_pct: embutidoPorGrupo.get(parseInt(group.group_number)) ?? null,
      vagas: group.vacancies == null ? null : parseVacancies(group.vacancies),
    }),
    [embutidoPorGrupo],
  );

  const contextoDoGrupo = useMemo(
    () => (group: ConsortiumGroup): ContextoDoGrupo => ({
      historico: historicoDoGrupo(group.group_number),
      parcelas: groupParcelas.get(parseInt(group.group_number)) ?? null,
    }),
    [historicoDoGrupo, groupParcelas],
  );

  /**
   * O que a busca está realmente procurando quando o modo é de lance livre.
   *
   * Sem esta linha o vendedor digita 260.000 e a lista devolve grupos de
   * 371.428 — parece defeito. O valor mostrado usa o embutido padrão; cada
   * grupo é testado com o embutido dele, por isso o "≈".
   */
  const avisoDoCredito = useMemo(() => {
    if (!modoUsaCreditoLiquido(filtros.modoLance)) return null;
    const bruto = creditoAlvoDoGrupo(filtros, { credit_min: null, credit_max: null });
    if (bruto == null) return null;
    return `Em ${ROTULO_MODO_LANCE[filtros.modoLance]}, o crédito é o valor líquido recebido pelo cliente. Busca por cartas de ≈ ${formatarBRL(bruto)} (embutido de ${EMBUTIDO_PADRAO_PCT}% ou o do grupo, quando informado).`;
  }, [filtros]);

  const itemsPerPage = 25;

  /**
   * O sync mais recente entre os grupos reais. Pegar o do 1º grupo da lista
   * mostrava a data de um grupo qualquer (e o mock de DEV sempre dizia "agora").
   */
  const ultimaSincronizacao = useMemo(() => {
    let maior: string | null = null;
    for (const g of groups ?? []) {
      if (g.last_sync_at && (!maior || Date.parse(g.last_sync_at) > Date.parse(maior))) {
        maior = g.last_sync_at;
      }
    }
    return maior;
  }, [groups]);

  const isMockGroupEnabled = Boolean(import.meta.env.DEV);

  const groupsWithMock = useMemo(() => {
    if (!groups) return groups;
    if (!isMockGroupEnabled) return groups;

    const hasMock = groups.some(group => String(group.group_number) === '9999');
    if (hasMock) return groups;

    const mockGroup: ConsortiumGroup = {
      id: -9999,
      group_number: '9999',
      segment: 'M',
      months: 40,
      assembly: 5,
      bid: 2,
      awarded: 1,
      vacancies: '10',
      flex: false,
      credit_min: 60000,
      credit_max: 60000,
      last_sync_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      contemplados_sorteio: 1,
      contemplados_lance: 2,
    };

    return [mockGroup, ...groups];
  }, [groups, isMockGroupEnabled]);


  // Carregar prazos e parcelas disponíveis nas categorias de cada grupo
  useEffect(() => {
    const loadGroupData = async () => {
      // Em páginas: são ~3 mil categorias; com o corte de 1000 do PostgREST
      // uns 2/3 dos grupos ficavam sem prazo e sem faixa de parcela.
      const { data: categories } = await todasAsLinhas<{
        group_number: number | string;
        prazo: number | null;
        total_parcela: number | string | null;
      }>(() =>
        supabase
          .from('servopa_group_categories')
          .select('group_number, prazo, total_parcela')
          .not('prazo', 'is', null),
      );

      if (categories) {
        const prazosMap = new Map<number, number[]>();
        const parcelasMap = new Map<number, { min: number; max: number }>();
        
        categories.forEach(cat => {
          const groupNum = Number(cat.group_number);
          
          // Processar prazos
          if (!prazosMap.has(groupNum)) {
            prazosMap.set(groupNum, []);
          }
          const prazo = cat.prazo;
          if (prazo && !prazosMap.get(groupNum)!.includes(prazo)) {
            prazosMap.get(groupNum)!.push(prazo);
          }
          
          // Processar parcelas
          if (cat.total_parcela) {
            const parcelaValue = typeof cat.total_parcela === 'string'
              ? parseFloat((cat.total_parcela as string).replace(',', '.'))
              : Number(cat.total_parcela);
            
            if (!isNaN(parcelaValue)) {
              if (!parcelasMap.has(groupNum)) {
                parcelasMap.set(groupNum, { min: parcelaValue, max: parcelaValue });
              } else {
                const current = parcelasMap.get(groupNum)!;
                parcelasMap.set(groupNum, {
                  min: Math.min(current.min, parcelaValue),
                  max: Math.max(current.max, parcelaValue)
                });
              }
            }
          }
        });
        
        if (isMockGroupEnabled) {
          prazosMap.set(9999, [40]);
          parcelasMap.set(9999, { min: 1500, max: 1900 });
        }
        
        setGroupPrazos(prazosMap);
        setGroupParcelas(parcelasMap);
      }

      // Embutido de cada grupo. Consulta à parte porque ele mora no cadastro
      // do grupo, não nas categorias — e quem não tiver linha aqui cai no
      // padrão da regra pura em vez de sumir da lista.
      const { data: detalhes } = await supabase
        .from('servopa_group_details')
        .select('group_number, lance_embutido');

      if (detalhes) {
        setEmbutidoPorGrupo(
          new Map(
            (detalhes as { group_number: number | string; lance_embutido: number | string | null }[])
              .map((d) => [Number(d.group_number), Number(d.lance_embutido)] as const)
              .filter(([, pct]) => Number.isFinite(pct)),
          ),
        );
      }
    };

    loadGroupData();
  }, [isMockGroupEnabled]);

  // Aplicar filtro de segmento via React Admin
  useEffect(() => {
    const filters = segmentoFilter === 'all' ? {} : { segment: segmentoFilter };
    setFilters(filters, {}, false); // false = don't reset pagination
  }, [segmentoFilter, setFilters]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, flexOnlyFilter, prazoExato, filtros]);

  // Extrair prazos únicos disponíveis nos grupos filtrados (antes do filtro de prazo)
  const prazosDisponiveis = useMemo(() => {
    if (!groupsWithMock) return [];

    // Primeiro aplicar filtros exceto o de prazo
    const gruposFiltrados = groupsWithMock.filter(group => {
      const searchLower = busca.toLowerCase();
      const matchesSearch = !busca || 
        String(group.group_number).includes(busca) ||
        String(group.group_number).toLowerCase().includes(searchLower) ||
        segmentoLabels[group.segment].toLowerCase().includes(searchLower) ||
        group.months.toString().includes(searchLower) ||
        group.assembly.toString().includes(searchLower) ||
        String(group.vacancies || '').toLowerCase().includes(searchLower) ||
        (group.flex ? 'flex' : 'não flex').includes(searchLower) ||
        (group.credit_min ? group.credit_min.toString().includes(searchLower) : false) ||
        (group.credit_max ? group.credit_max.toString().includes(searchLower) : false);

      if (flexOnlyFilter === 'sim' && !group.flex) return false;
      if (flexOnlyFilter === 'nao' && group.flex) return false;

      if (!grupoPassaNosFiltrosAvancados(grupoParaFiltro(group), filtros, contextoDoGrupo(group))) {
        return false;
      }

      return matchesSearch;
    });

    // Coletar todos os prazos únicos desses grupos
    const prazosSet = new Set<number>();
    gruposFiltrados.forEach(group => {
      const groupNum = typeof group.group_number === 'string' ? parseInt(group.group_number) : group.group_number;
      const prazosDoGrupo = groupPrazos.get(groupNum) || [];
      prazosDoGrupo.forEach(prazo => prazosSet.add(prazo));
    });

    return Array.from(prazosSet).sort((a, b) => a - b);
  }, [groupsWithMock, groupPrazos, busca, flexOnlyFilter, filtros, grupoParaFiltro, contextoDoGrupo]);

  // Aplicar apenas filtro de busca (segmento já é filtrado pelo servidor)
  const filteredAndSortedGroups = useMemo(() => {
    if (!groupsWithMock) return [];

    const filtered = groupsWithMock.filter(group => {
      // Filtro de busca - match against everything
      const searchLower = busca.toLowerCase();
      const matchesSearch = !busca || 
        String(group.group_number).includes(busca) || // Exact match for group number
        String(group.group_number).toLowerCase().includes(searchLower) ||
        segmentoLabels[group.segment].toLowerCase().includes(searchLower) ||
        group.months.toString().includes(searchLower) ||
        group.assembly.toString().includes(searchLower) ||
        String(group.vacancies || '').toLowerCase().includes(searchLower) ||
        (group.flex ? 'flex' : 'não flex').includes(searchLower) ||
        (group.credit_min ? group.credit_min.toString().includes(searchLower) : false) ||
        (group.credit_max ? group.credit_max.toString().includes(searchLower) : false);

      // Filtro Flex Only
      if (flexOnlyFilter === 'sim') {
        if (!group.flex) {
          return false;
        }
      } else if (flexOnlyFilter === 'nao') {
        if (group.flex) {
          return false;
        }
      }

      // Filtro de prazo exato - filtra grupos que TÊM categorias com esse prazo
      if (prazoExato && prazoExato !== 'all') {
        const groupNum = typeof group.group_number === 'string' ? parseInt(group.group_number) : group.group_number;
        const prazosDoGrupo = groupPrazos.get(groupNum) || [];
        if (!prazosDoGrupo.includes(parseInt(prazoExato))) {
          return false;
        }
      }

      if (!grupoPassaNosFiltrosAvancados(grupoParaFiltro(group), filtros, contextoDoGrupo(group))) {
        return false;
      }

      return matchesSearch;
    });

    // Aplicar ordenação
    const sorted = [...filtered].sort((a, b) => {
    let compareValue = 0;
    
    switch (sortBy) {
      case 'group_number':
        compareValue = String(a.group_number).localeCompare(String(b.group_number));
        break;
      case 'vacancies': {
        const aVagas = parseVacancies(a.vacancies);
        const bVagas = parseVacancies(b.vacancies);
        compareValue = aVagas - bVagas;
        break;
      }
      case 'prazo':
        compareValue = a.months - b.months;
        break;
      case 'credit_min':
        compareValue = (a.credit_min || 0) - (b.credit_min || 0);
        break;
      case 'credit_max':
        compareValue = (a.credit_max || 0) - (b.credit_max || 0);
        break;
    }
    
    return sortOrder === 'asc' ? compareValue : -compareValue;
  });

  return sorted;
}, [groupsWithMock, busca, flexOnlyFilter, prazoExato, filtros, sortBy, sortOrder, groupPrazos, grupoParaFiltro, contextoDoGrupo]);

  // Pagination
  const totalFiltered = filteredAndSortedGroups.length;
  const totalPages = Math.ceil(totalFiltered / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedGroups = filteredAndSortedGroups.slice(startIndex, endIndex);

  const handleGroupClick = (groupNumber: number) => {
    setSelectedGroupNumber(groupNumber);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedGroupNumber(null);
  };

  if (isPending) {
    return <PageSpinner />;
  }

  if (error) {
    return <div className="text-red-500 p-4">Não foi possível carregar os grupos.</div>;
  }

  if (!groupsWithMock || groupsWithMock.length === 0) {
    return (
      <div className="text-center text-muted-foreground p-8">
        {segmentoFilter !== 'all' 
          ? `Nenhum grupo de ${segmentoLabels[segmentoFilter]} encontrado.`
          : 'Nenhum grupo disponível.'}
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 pb-8">
      <CabecalhoPagina titulo="Grupos" />

      {/* Filtros */}
      <div className="cartao bg-card p-4">
        <h3 className="mb-4 text-sm font-semibold tracking-[-0.005em] text-marinho">Filtros e busca</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Busca */}
          <div>
            <Label htmlFor="search">Buscar</Label>
            <Input
              id="search"
              placeholder="Número do grupo, nome..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mt-1 border-muted-foreground/30 bg-muted/40 focus-visible:bg-background dark:bg-background"
            />
          </div>

          {/* Filtro Segmento */}
          <div>
            <Label htmlFor="segmento">Segmento</Label>
            <Select value={segmentoFilter} onValueChange={setSegmentoFilter}>
              <SelectTrigger
                id="segmento"
                className="mt-1 border-muted-foreground/30 bg-muted/40 focus-visible:bg-background dark:bg-background"
              >
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="I">Imóveis</SelectItem>
                <SelectItem value="M">Automóveis</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Ordenar Por */}
          <div>
            <Label htmlFor="sortBy">Ordenar por</Label>
            <Select
              value={sortBy}
              onValueChange={(value) => salvarOrdenacao({ ...ordenacao, campo: value as CampoDeOrdenacao })}
            >
              <SelectTrigger
                id="sortBy"
                className="mt-1 border-muted-foreground/30 bg-muted/40 focus-visible:bg-background dark:bg-background"
              >
                <SelectValue placeholder="Número do grupo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="group_number">Número do grupo</SelectItem>
                <SelectItem value="vacancies">Vagas</SelectItem>
                <SelectItem value="prazo">Prazo total</SelectItem>
                <SelectItem value="credit_min">Crédito mínimo</SelectItem>
                <SelectItem value="credit_max">Crédito máximo</SelectItem>
              </SelectContent>
            </Select>
            <button
              onClick={() => salvarOrdenacao({ ...ordenacao, ordem: sortOrder === 'asc' ? 'desc' : 'asc' })}
              className="mt-2 text-left text-xs text-muted-foreground hover:text-foreground underline"
            >
              {sortOrder === 'asc' ? 'Crescente' : 'Decrescente'}
            </button>
          </div>

          {/* Filtro Flex */}
          <div>
            <Label className="text-sm">Tipo de grupo</Label>
            <button
              onClick={() => setFlexOnlyFilter(flexOnlyFilter === 'sim' ? 'all' : 'sim')}
              className="flex flex-col items-center gap-1.5 group mt-1 cursor-pointer"
            >
              <div className={`relative w-12 h-6 rounded-full transition-colors ${
                flexOnlyFilter === 'sim' ? 'bg-marinho' : 'bg-gray-300'
              }`}>
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
                  flexOnlyFilter === 'sim' ? 'translate-x-6' : 'translate-x-0'
                }`} />
              </div>
              <span className="text-xs text-muted-foreground">Flex</span>
            </button>
          </div>
        </div>

        {/* Filtro de Prazo - Nova linha */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="prazoFilter" className="text-sm">Filtrar por prazo (meses)</Label>
            <Select value={prazoExato || 'all'} onValueChange={(value) => setPrazoExato(value === 'all' ? '' : value)}>
              <SelectTrigger
                id="prazoFilter"
                className="mt-1 max-w-xs border-muted-foreground/30 bg-muted/40 focus-visible:bg-background dark:bg-background"
              >
                <SelectValue placeholder="Todos os prazos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {prazosDisponiveis.map(prazo => (
                  <SelectItem key={prazo} value={String(prazo)}>
                    {prazo} meses
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Botão para expandir Filtros Avançados */}
        <div className="mt-4 pt-4 border-t">
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="flex items-center justify-between w-full text-sm font-semibold hover:text-primary transition-colors"
          >
            <span>Filtros avançados</span>
            <svg
              className={`w-5 h-5 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Filtros Avançados - Colapsáveis */}
          {showAdvancedFilters && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 animate-in slide-in-from-top-2">
              {/* Teto de lance. Dois campos porque são duas perguntas: a média é
                  o que se espera pagar; o mínimo é o melhor caso já observado.
                  Grupo sem histórico suficiente sai da lista quando qualquer um
                  dos dois está preenchido — ver MIN_ASSEMBLEIAS. */}
              <div>
                <Label htmlFor="maxLanceMedio" className="text-xs">Lance médio até (%)</Label>
                <Input
                  id="maxLanceMedio"
                  type="number"
                  placeholder="ex: 25"
                  value={filtros.maxLanceMedio}
                  onChange={(e) => definirFiltro('maxLanceMedio', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="maxLanceMin" className="text-xs">Lance mínimo até (%)</Label>
                <Input
                  id="maxLanceMin"
                  type="number"
                  placeholder="ex: 15"
                  value={filtros.maxLanceMin}
                  onChange={(e) => definirFiltro('maxLanceMin', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="modoLance" className="text-xs">Modo de lance</Label>
                <Select
                  value={filtros.modoLance}
                  onValueChange={(valor) => definirFiltro('modoLance', valor as ModoLance)}
                >
                  <SelectTrigger id="modoLance" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(ROTULO_MODO_LANCE) as ModoLance[]).map((modo) => (
                      <SelectItem key={modo} value={modo}>{ROTULO_MODO_LANCE[modo]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Um campo só de crédito: o cliente sabe quanto quer RECEBER, não
                  a faixa da carta. Nos leilões livres esse valor é o líquido e a
                  busca procura a carta cheia correspondente — o aviso abaixo
                  mostra a conta, porque um número que muda sozinho sem
                  explicação parece defeito. */}
              <div>
                <Label htmlFor="credito" className="text-xs">Crédito</Label>
                <MoedaTextoInput
                  id="credito"
                  value={filtros.credito}
                  onValueChange={(v) => definirFiltro('credito', v)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="maxParcela" className="text-xs">Parcela máx.</Label>
                <MoedaTextoInput
                  id="maxParcela"
                  value={filtros.maxParcela}
                  onValueChange={(v) => definirFiltro('maxParcela', v)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="minVagas" className="text-xs">Vagas (mín)</Label>
                <Input
                  id="minVagas"
                  type="number"
                  placeholder="0"
                  value={filtros.minVagas}
                  onChange={(e) => definirFiltro('minVagas', e.target.value)}
                  className="mt-1"
                />
              </div>
              {avisoDoCredito && (
                <p className="col-span-full text-xs text-muted-foreground">{avisoDoCredito}</p>
              )}
            </div>
          )}
        </div>

        {/* Botão para Limpar Filtros */}
        <div className="mt-4 flex items-center gap-2">
          <button
            onClick={() => {
              setSearchTerm('');
              setSegmentoFilter('all');
              setFlexOnlyFilter('all');
              setPrazoExato('');
              setFiltros(FILTROS_AVANCADOS_VAZIOS);
              salvarOrdenacao(ORDENACAO_PADRAO);
            }}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            Limpar todos os filtros
          </button>
        </div>
      </div>

      {/* O Ajudante de lance (escolher o grupo pela chance de contemplar)
          saiu em 14/10/2026: o ranking não leva em conta os motivos de um
          grupo contemplar rápido que o Lucas explicou na reunião. Volta
          quando esses critérios entrarem (lances/AjudanteLanceSimulador.tsx). */}

      {/* Grupos numa tabela só (Premium North), um grupo por linha, com os
          mesmos campos do cartão de antes. Nunca rola de lado: com menos de
          ~900px de conteúdo cada linha vira um bloco com rótulo e valor. */}
      <div className="cartao @container bg-card">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm text-muted-foreground">
          <div className="flex flex-wrap items-center gap-3">
            <span>
              Mostrando {startIndex + 1}-{Math.min(endIndex, totalFiltered)} de {totalFiltered} grupos (Página {currentPage} de {totalPages})
            </span>
            {segmentoFilter !== 'all' && (
              <span className="rounded bg-[#EEF1F6] px-2 py-1 text-xs font-medium text-primary">
                Filtro ativo: {segmentoFilter === 'M' ? 'Automóveis' : 'Imóveis'}
              </span>
            )}
          </div>
          {ultimaSincronizacao && (
            <span className="text-xs">
              Última sincronização: {formatBrDateTime(ultimaSincronizacao)}
            </span>
          )}
        </div>

        {paginatedGroups.length === 0 ? (
          <div className="border-t border-border p-8 text-center text-muted-foreground">
            Nenhum grupo encontrado com os filtros aplicados.
          </div>
        ) : (
          <>
            <div className="hidden border-y border-border px-4 py-2 text-xs font-medium text-muted-foreground @4xl:grid @4xl:grid-cols-[minmax(0,2fr)_repeat(5,minmax(0,1fr))_20px] @4xl:gap-x-4">
              <span>Grupo</span>
              <span>Meses</span>
              <span className="text-right">Crédito mínimo</span>
              <span className="text-right">Crédito máximo</span>
              <span className="text-right">Vagas</span>
              <span>Flex</span>
              <span />
            </div>
            {paginatedGroups.map((group) => {
              const rapido = contemplaRapido.has(parseInt(group.group_number));
              return (
                <div
                  key={group.id}
                  data-grupo={group.group_number}
                  role="button"
                  tabIndex={0}
                  className="grid cursor-pointer grid-cols-2 gap-x-4 gap-y-2 border-b border-[#EDF0F4] px-4 py-3 text-sm transition-colors last:border-b-0 hover:bg-[#FAFBFC] focus-visible:bg-[#FAFBFC] focus-visible:outline-none @4xl:grid-cols-[minmax(0,2fr)_repeat(5,minmax(0,1fr))_20px] @4xl:items-center"
                  onClick={() => handleGroupClick(parseInt(group.group_number))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleGroupClick(parseInt(group.group_number));
                    }
                  }}
                >
                  <div className="col-span-2 flex flex-wrap items-center gap-2 @4xl:col-span-1">
                    <span className="font-mono text-[15px] font-semibold text-marinho">Grupo {group.group_number}</span>
                    <span className="rounded px-2 py-0.5 text-[11px] font-semibold bg-[#EEF1F6] text-primary">
                      {segmentoLabels[group.segment]}
                    </span>
                    {rapido && (
                      <span
                        className="inline-flex items-center gap-1 rounded bg-[#DDF5E6] px-2 py-0.5 text-[11px] font-semibold text-[#0E7A3E] before:h-1.5 before:w-1.5 before:rounded-full before:bg-current"
                        title="Entre os 20% do segmento com maior chance de uma cota ser contemplada por mês, nas 3 últimas assembleias (sorteio e todos os lances)."
                      >
                        Contempla rápido
                      </span>
                    )}
                  </div>

                  {/* Lance típico, ritmo de contemplação e concorrência ficam no
                      detalhe do grupo (Rick, 14/10: na lista só confundiam). */}

                  <div>
                    <span className="block text-xs text-muted-foreground @4xl:hidden">Meses</span>
                    <span className="font-medium tabular-nums">{group.assembly}/{group.months}</span>
                  </div>
                  <div className="@4xl:text-right">
                    <span className="block text-xs text-muted-foreground @4xl:hidden">Crédito mínimo</span>
                    <span className="font-medium tabular-nums">
                      {group.credit_min ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(group.credit_min) : '-'}
                    </span>
                  </div>
                  <div className="@4xl:text-right">
                    <span className="block text-xs text-muted-foreground @4xl:hidden">Crédito máximo</span>
                    <span className="font-medium tabular-nums">
                      {group.credit_max ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(group.credit_max) : '-'}
                    </span>
                  </div>
                  <div className="@4xl:text-right">
                    <span className="block text-xs text-muted-foreground @4xl:hidden">Vagas</span>
                    <span className="font-semibold tabular-nums text-[#0E7A3E]">{group.vacancies}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-muted-foreground @4xl:hidden">Flex</span>
                    <span className="font-medium">{group.flex ? 'Sim' : 'Não'}</span>
                  </div>
                  <ChevronRight className="hidden h-4 w-4 text-muted-foreground @4xl:block" aria-hidden />
                </div>
              );
            })}
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-center gap-2 border-t border-border px-4 py-3">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="cursor-pointer rounded-[7px] border border-border px-3 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50 hover:bg-muted"
            >
              Anterior
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`cursor-pointer rounded-[7px] border px-3 py-1 text-sm tabular-nums ${
                  currentPage === page
                    ? 'border-marinho bg-marinho text-white'
                    : 'border-border hover:bg-muted'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="cursor-pointer rounded-[7px] border border-border px-3 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50 hover:bg-muted"
            >
              Próxima
            </button>
          </div>
        )}
      </div>

      {/* Modal de Detalhes */}
      <GroupDetailModal
        groupNumber={selectedGroupNumber}
        open={modalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default {
  list: ConsortiumGroupList,
};
